/**
 * Shop portal sort whitelists — must match backend shop endpoints (FR-006).
 * Reference: specs/010-shop-portal-column-sort/contracts/shop-portal-sort-api.md
 */
import type { ColumnSortSpec } from '../hooks/useTableSort';

export type TableSortKey =
  | 'shopCreditTransactions'
  | 'shopUsdLedger'
  | 'shopCashDeposits'
  | 'shopCashRedemptions'
  | 'shopWithdrawals'
  | 'shopPlayers'
  | 'shopGameWallets';

export const TABLE_SORT_CONFIG: Record<TableSortKey, ColumnSortSpec[]> = {
  // GET /api/shop/transactions
  shopCreditTransactions: [
    { key: 'created', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
    { key: 'amount', label: 'Amount', valueKind: 'money', magnitude: true, defaultDirection: 'DESC' },
    { key: 'from', label: 'From', valueKind: 'text', defaultDirection: 'ASC' },
    { key: 'to', label: 'To', valueKind: 'text', defaultDirection: 'ASC' },
  ],

  // GET /api/shop/online-balance/ledger (buildUsdLedgerOrder aliases)
  shopUsdLedger: [
    { key: 'amount', label: 'Amount', valueKind: 'money', magnitude: true, defaultDirection: 'DESC' },
    { key: 'createdAt', label: 'Created', valueKind: 'datetime', defaultDirection: 'DESC' },
    { key: 'balanceAfter', label: 'Balance after', valueKind: 'money', defaultDirection: 'DESC' },
  ],

  // GET /api/shop/cash-transactions — no Created column on shop portal (FR-005)
  shopCashDeposits: [
    { key: 'creditAmount', label: 'Credits', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'cashReceived', label: 'Cash', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'buyerCostRate', label: 'Cost', valueKind: 'money', defaultDirection: 'DESC' },
  ],

  shopCashRedemptions: [
    { key: 'creditAmount', label: 'Credits', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'cashReceived', label: 'Cash', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'buyerCostRate', label: 'Cost', valueKind: 'money', defaultDirection: 'DESC' },
  ],

  // GET /api/shop/withdrawals — mirror admin withdrawal queue (008)
  shopWithdrawals: [
    { key: 'createdAt', label: 'Time', valueKind: 'datetime', defaultDirection: 'DESC' },
    { key: 'amount', label: 'Amount', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'status', label: 'Status', valueKind: 'ranked', defaultDirection: 'ASC' },
  ],

  // GET /api/shop/players — query.sort (shop_assigned_at | name | last_login)
  shopPlayers: [
    { key: 'name', label: 'Username', valueKind: 'text', defaultDirection: 'ASC' },
    { key: 'balance', label: 'Balance', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'shop_assigned_at', label: 'Date joined', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],

  // GET /api/shop/game-wallets — sortBy walletBalance | inGameBalance | lastSyncedAt
  shopGameWallets: [
    { key: 'walletBalance', label: 'Temp wallet balance', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'inGameBalance', label: 'In-game balance', valueKind: 'money', defaultDirection: 'DESC' },
    { key: 'lastSyncedAt', label: 'Last sync', valueKind: 'datetime', defaultDirection: 'DESC' },
  ],
};

export function getTableSortColumns(table: TableSortKey): ColumnSortSpec[] {
  return TABLE_SORT_CONFIG[table];
}
